import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { notificationSound } from './utils/notificationSound';
// v1.4 RBAC 阶段 4 P0：权限点 Context + 启动时 /auth/me 同步
import { PermissionProvider } from './lib/permissions';
import { $ } from './lib/api';
import type { AuthUser } from './store/auth';
import { LoginPage } from './pages/Login';
import { ChangePasswordPage } from './pages/ChangePassword';
import { AppLayout } from './pages/Layout';
import { DashboardPage } from './pages/Dashboard';

/** #5: /dashboard 按 role 分流 — academic 静默重定向到 /academic/dashboard */
function DashboardOrAcademic() {
  const user = useAuthStore((s) => s.user);
  if (user && (user.role === 'academic' || user.role === 'academic_director')) {
    // bug收尾批#5：原拟豁免财务总监(程华庆)访问 /dashboard 看财务首页，但 DirectorFinanceDashboardView 依赖
    //   dashboard:director_view + report:view_team + report:view_all 三个端点权限，程华庆均无(seed 0024/0029 只授 approval/invoice:approve/payroll)→403 空壳。
    //   故不在此放行(避免破壳)；其财务入口走【财务工作台页 /admin/finance-workbench】(用 invoice:approve、他持有、真能用)。
    //   要给他首页财务 KPI 看板需 seed 授上述 3 个点(本批无迁移、已 flag 待程伟拍)。
    return <Navigate to="/academic/dashboard" replace />;
  }
  return <DashboardPage />;
}
import { CustomersListPage } from './pages/customers/CustomersList';
import { CustomerDetailPage } from './pages/customers/CustomerDetail';
import { NewFollowUpPage } from './pages/follow-ups/NewFollowUp';
import { FollowUpsListPage } from './pages/follow-ups/FollowUpsList';
import { PoolPage } from './pages/Pool';
import { ContractsListPage } from './pages/biz/ContractsList';
import { InvoicesListPage } from './pages/biz/InvoicesList';
import { PaymentsListPage } from './pages/biz/PaymentsList';
import { ShipmentsListPage } from './pages/biz/ShipmentsList';
import { MaterialsListPage } from './pages/biz/MaterialsList';
import { OrdersListPage } from './pages/biz/OrdersList';
import { ApprovalsPage } from './pages/Approvals';
import { RescuePage } from './pages/Rescue';
import { NotificationsPage } from './pages/Notifications';
import { TeamPage } from './pages/Team';
import { DirectorOverviewPage } from './pages/DirectorOverview';
import { ReportsPage } from './pages/Reports';
import { TeamBoardPage } from './pages/salary/TeamBoard';
import { RouteGuard } from './components/RouteGuard';
import { AdminUsersPage } from './pages/admin/Users';
import { AdminTeamsPage } from './pages/admin/Teams';
import { DataImportPage } from './pages/imports/DataImport';
import { SandboxPreviewPage } from './pages/imports/SandboxPreview';
import { ScreenshotImportPage } from './pages/imports/ScreenshotImport';
import { AdminAiConfigsPage } from './pages/admin/AiConfigs';
import { AdminSystemConfigsPage } from './pages/admin/SystemConfigs';
import { AdminSalesTargetsPage } from './pages/admin/SalesTargets';
import { AdminTestCenterPage } from './pages/admin/TestCenter';
import { LeadRoutingConfigPage } from './pages/admin/LeadRoutingConfig';
import { ReconcileCenterPage } from './pages/admin/ReconcileCenter';
import { StaffPerformancePage } from './pages/StaffPerformance';
import { FinanceWorkbenchPage } from './pages/admin/FinanceWorkbench';
import { TransferLogsPage } from './pages/admin/TransferLogs';
import { LeadMonitorCenterPage } from './pages/admin/LeadMonitorCenter';
import { ReservePoolPage } from './pages/admin/ReservePool';
import { LifecycleConfigPage } from './pages/admin/LifecycleConfig';
import { VoidageRequestsPage } from './pages/manager/VoidageRequests';
import { BroadcastCampaignsPage } from './pages/manager/BroadcastCampaigns';
import { CustomerCreationRequestsPage } from './pages/manager/CustomerCreationRequests';
import { MessengerPage } from './pages/Messenger';
import { SettingsPage } from './pages/Settings';
import { ControlPanelPage } from './pages/admin/ControlPanel';
import {
  StudentsListPage as AcademicStudentsListPage,
  StudentDetailPage as AcademicStudentDetailPage,
  OverviewPage as AcademicOverviewPage,
  ReportsPage as AcademicReportsPage,
  DashboardPage as AcademicDashboardPage,
  TasksPage as AcademicTasksPage,
} from './pages/academic/AcademicPages';
import { WeworkArchivePage } from './pages/admin/WeworkArchive';
import { AboutPage } from './pages/About';
import { AdAttributionPage } from './pages/reports/AdAttribution';
import { CostImportPage } from './pages/investment/CostImport';
import { MarketingAccountsPage } from './pages/marketing/Accounts';
import { MarketingLeadsListPage, MarketingLeadDetailPage } from './pages/marketing/Leads';
import { MarketingDashboardPage } from './pages/marketing/MarketingDashboard';
import { XhsOAuthConfigPage } from './pages/marketing/XhsOAuthConfig';
import { XhsNoteLeaderboardPage } from './pages/marketing/XhsNoteLeaderboard';
import { CustomerPoolV2Page } from './pages/customers/CustomerPoolV2';
import { AiCoachPage } from './pages/AiCoach';
import { AiReportsPage } from './pages/AiReportsPage';
import { AiInsightsAdminPage } from './pages/AiInsightsAdmin';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const mustChange = useAuthStore((s) => s.mustChangePassword);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace />;
  // 强制改密 guard：登录后 mustChangePassword=true 时除 /change-password 外全部重定向
  if (mustChange && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
}

export function App() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setUser = useAuthStore((s) => s.setUser);

  // 首次任意用户交互 → 解锁音频自动播放策略
  useEffect(() => {
    const handler = () => {
      notificationSound.unlock();
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
    window.addEventListener('click', handler);
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
  }, []);

  // v1.4 RBAC 阶段 4 P0：启动 / token 变化时拉 /auth/me 同步 user.permissions
  // 覆盖 ① 老 token 的 user 没 permissions 字段 ② admin 改了权限在线用户拿新值
  useEffect(() => {
    if (!accessToken) return;
    $.get<AuthUser>('/auth/me').then((u) => setUser(u)).catch(() => { /* 失败由 axios interceptor 弹 toast */ });
  }, [accessToken, setUser]);

  // IM 提示音偏好：登录后拉 /me/settings 同步到播放器（跨设备一致；失败用 localStorage 兜底）
  useEffect(() => {
    if (!accessToken) return;
    $.get<any>('/me/settings').then((s) => {
      // 存量IM8：拿到服务端偏好就应用(用户所选铃声)；即使没有 im_sound 也 configure 一次确保 rebuild/解锁链执行。
      notificationSound.configure(s?.im_sound ?? {});
    }).catch((e) => {
      // 存量IM8：失败【不静默吞】——保证 configure 至少执行一次(rebuild)。★用 configure({}) 保留 localStorage 已缓存的
      //   上次成功偏好(singleton 构造时已从 LS 读)，不硬塞 default ding(否则瞬时网络失败会覆盖用户已选铃声=更糟)。
      console.warn('[im-sound] /me/settings 加载失败，沿用本地缓存铃声偏好:', e?.message ?? e);
      notificationSound.configure({});
    });
  }, [accessToken]);

  return (
    <PermissionProvider>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* 改密页 — RequireAuth 包裹但 AppLayout 之外（不显示 sidebar/顶栏，防绕过） */}
      <Route
        path="/change-password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        {/* #5: academic 角色访问 /dashboard 静默跳教务工作台（不弹"无权"toast，因为是默认落地页） */}
        <Route path="dashboard" element={<DashboardOrAcademic />} />
        {/* v1.2-rc2 ⑤：销售流程 8 路由对 academic / academic_director 重定向 — 教务无权访问销售页 */}
        <Route path="customers" element={
          <RouteGuard requirePermissions="customer:view" redirectTo="/academic/students">
            <CustomersListPage />
          </RouteGuard>
        } />
        <Route path="customers/:id" element={
          <RouteGuard requirePermissions="customer:view" redirectTo="/academic/students">
            <CustomerDetailPage />
          </RouteGuard>
        } />
        <Route path="follow-ups" element={
          <RouteGuard requirePermissions="follow_up:view" redirectTo="/academic/students">
            <FollowUpsListPage />
          </RouteGuard>
        } />
        <Route path="follow-ups/new" element={
          <RouteGuard requirePermissions="follow_up:create" redirectTo="/academic/students">
            <NewFollowUpPage />
          </RouteGuard>
        } />
        <Route path="pool" element={
          <RouteGuard requirePermissions="customer:view_all_pool" redirectTo="/academic/students">
            <PoolPage />
          </RouteGuard>
        } />
        <Route path="customers/pool" element={
          <RouteGuard requirePermissions="customer:view_all_pool" redirectTo="/academic/students">
            <CustomerPoolV2Page />
          </RouteGuard>
        } />
        <Route path="contracts" element={
          <RouteGuard requirePermissions="contract:view" redirectTo="/academic/students">
            <ContractsListPage />
          </RouteGuard>
        } />
        <Route path="invoices" element={
          <RouteGuard requirePermissions="invoice:view" redirectTo="/academic/students">
            <InvoicesListPage />
          </RouteGuard>
        } />
        <Route path="payments" element={
          <RouteGuard requirePermissions="payment:view" redirectTo="/academic/students">
            <PaymentsListPage />
          </RouteGuard>
        } />
        <Route path="shipments" element={
          <RouteGuard allowedRoles={['sales','manager','director','admin']} redirectTo="/academic/students">
            <ShipmentsListPage />
          </RouteGuard>
        } />
        <Route path="materials" element={
          <RouteGuard allowedRoles={['sales','manager','director','admin']} redirectTo="/academic/students">
            <MaterialsListPage />
          </RouteGuard>
        } />
        <Route path="orders" element={
          <RouteGuard requirePermissions="order:view" redirectTo="/academic/students">
            <OrdersListPage />
          </RouteGuard>
        } />
        {/* 薪酬阶段3：团队薪酬看板/发薪台账。★纯按 payroll:view 权限点放行(不锁 allowedRoles)★——
            finance 程华庆 role=academic_director(非 director)，靠 seed0029 用户级授权才有 payroll:view；用角色白名单会把他挡掉。
            可见 = admin / director(牛水停) / manager / finance(程华庆)。后端 actor.role 行级过滤：manager 仅本团队、其余全销售。 */}
        <Route path="salary/team-board" element={
          <RouteGuard requirePermissions="payroll:view" redirectTo="/dashboard">
            <TeamBoardPage />
          </RouteGuard>
        } />
        {/* v1.2-rc2 #6 同类：原裸装路由补 RouteGuard，academic 一律重定向到教务对接 */}
        {/* 审批/救援/团队的 allowedRoles 与 Layout.tsx 已有 hide 规则一致（不含 sales / academic） */}
        {/* B2 件1：academic_director（程华庆）是发票审批人，放行进审批中心 */}
        <Route path="approvals" element={
          <RouteGuard requirePermissions="approval:view" redirectTo="/academic/students">
            <ApprovalsPage />
          </RouteGuard>
        } />
        <Route path="rescue" element={
          <RouteGuard requirePermissions="rescue:view" redirectTo="/academic/students">
            <RescuePage />
          </RouteGuard>
        } />
        {/* notifications：所有登录用户都该看自己通知（含 academic），不拦 */}
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="team" element={
          <RouteGuard requirePermissions="user:view" redirectTo="/academic/students">
            <TeamPage />
          </RouteGuard>
        } />
        <Route path="director" element={
          <RouteGuard requirePermissions="dashboard:director_view">
            <DirectorOverviewPage />
          </RouteGuard>
        } />
        <Route path="reports" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <ReportsPage />
          </RouteGuard>
        } />
        {/* v1.2-rc2 #6：AI 教练同后端 @Roles 限制（不含 academic）；前端路由层先拦，避免渲染 UI 才被 API 403 */}
        <Route path="ai-coach" element={
          <RouteGuard requirePermissions="ai:coach_view" redirectTo="/academic/students">
            <AiCoachPage />
          </RouteGuard>
        } />
        {/* 大权限批/bug9 收口：去角色白名单，纯按 user:create 权限点放行（同 Layout.tsx 菜单门控 + salary/team-board 范式）。
            manager 经 migration 0076 授 user:create → 可进；后端 users 端点已加 team-scope+防提权双闸。 */}
        <Route path="admin/users" element={
          <RouteGuard requirePermissions="user:create">
            <AdminUsersPage />
          </RouteGuard>
        } />
        <Route path="admin/teams" element={
          <RouteGuard allowedRoles={['admin']}>
            <AdminTeamsPage />
          </RouteGuard>
        } />
        {/* 数据导入中心：4 入口共享同一页面（页面内按 role 区分文案/权限） */}
        <Route path="admin/data-import" element={<DataImportPage />} />
        <Route path="admin/data-import/sandbox/:id" element={<SandboxPreviewPage />} />
        <Route path="admin/data-import/screenshots" element={<ScreenshotImportPage />} />
        <Route path="team/data-import" element={<DataImportPage />} />
        <Route path="team/data-import/sandbox/:id" element={<SandboxPreviewPage />} />
        <Route path="team/data-import/screenshots" element={<ScreenshotImportPage />} />
        <Route path="import" element={<DataImportPage />} />
        <Route path="import/sandbox/:id" element={<SandboxPreviewPage />} />
        <Route path="import/screenshots" element={<ScreenshotImportPage />} />
        <Route path="admin/ai-configs" element={<Navigate to="/admin/control-panel?tab=ai" replace />} />
        <Route path="admin/system-configs" element={<Navigate to="/admin/control-panel?tab=business" replace />} />
        <Route path="admin/ai-configs-legacy" element={<AdminAiConfigsPage />} />
        <Route path="admin/system-configs-legacy" element={<AdminSystemConfigsPage />} />
        <Route path="admin/sales-targets" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <AdminSalesTargetsPage />
          </RouteGuard>
        } />
        <Route path="manager/sales-targets" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <AdminSalesTargetsPage />
          </RouteGuard>
        } />
        <Route path="admin/test-center" element={
          <RouteGuard requirePermissions="system:test_center">
            <AdminTestCenterPage />
          </RouteGuard>
        } />
        {/* v1.4 RBAC P1-#3：director 能进此页只读（lead:routing_view）；保存按钮内部由 useHasPermission 禁用 */}
        <Route path="admin/lead-routing-config" element={
          <RouteGuard requirePermissions="lead:routing_view">
            <LeadRoutingConfigPage />
          </RouteGuard>
        } />
        <Route path="marketing/accounts" element={
          <RouteGuard requirePermissions="marketing:account_manage">
            <MarketingAccountsPage />
          </RouteGuard>
        } />
        <Route path="marketing/xhs-oauth-config" element={
          <RouteGuard requirePermissions="marketing:account_manage">
            <XhsOAuthConfigPage />
          </RouteGuard>
        } />
        <Route path="marketing/xhs-notes" element={
          <RouteGuard requirePermissions="marketing:dashboard_view">
            <XhsNoteLeaderboardPage />
          </RouteGuard>
        } />
        <Route path="marketing/dashboard" element={
          <RouteGuard requirePermissions="marketing:dashboard_view">
            <MarketingDashboardPage />
          </RouteGuard>
        } />
        <Route path="marketing/leads" element={
          <RouteGuard requirePermissions="lead:view">
            <MarketingLeadsListPage />
          </RouteGuard>
        } />
        <Route path="marketing/leads/:id" element={
          <RouteGuard requirePermissions="lead:view">
            <MarketingLeadDetailPage />
          </RouteGuard>
        } />
        <Route path="marketing/reconcile" element={
          <RouteGuard requirePermissions="marketing:reconcile">
            <ReconcileCenterPage />
          </RouteGuard>
        } />
        <Route path="staff-performance" element={<StaffPerformancePage />} />
        {/* bug收尾批#5：财务总监(程华庆 academic_director+director_type='finance')按维度放行，不再被角色白名单挡 */}
        <Route path="admin/finance-workbench" element={
          <RouteGuard allowedRoles={['admin', 'director']} allowDirectorTypes={['finance']}>
            <FinanceWorkbenchPage />
          </RouteGuard>
        } />
        <Route path="admin/transfer-logs" element={
          <RouteGuard allowedRoles={['admin', 'director']}>
            <TransferLogsPage />
          </RouteGuard>
        } />
        <Route path="admin/lead-monitor-center" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <LeadMonitorCenterPage />
          </RouteGuard>
        } />
        <Route path="admin/reserve-pool" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <ReservePoolPage />
          </RouteGuard>
        } />
        <Route path="admin/lifecycle-config" element={
          <Navigate to="/admin/control-panel?tab=business" replace />
        } />
        {/* v1.5 P2-7：control-panel 仅 admin/director（移除 manager；后端 PATCH lifecycle 已收紧 configs_manage） */}
        <Route path="admin/control-panel" element={
          <RouteGuard allowedRoles={['admin', 'director']} requirePermissions="system:configs_manage">
            <ControlPanelPage />
          </RouteGuard>
        } />

        {/* v1.2 Batch 3：教务模块 5 个页面 */}
        <Route path="academic/students" element={
          <RouteGuard allowedRoles={['academic', 'academic_director', 'admin', 'director', 'manager']}>
            <AcademicStudentsListPage />
          </RouteGuard>
        } />
        <Route path="academic/students/:id" element={
          <RouteGuard allowedRoles={['academic', 'academic_director', 'admin', 'director', 'manager']}>
            <AcademicStudentDetailPage />
          </RouteGuard>
        } />
        <Route path="academic/overview" element={
          <RouteGuard requirePermissions="academic:view_overview">
            <AcademicOverviewPage />
          </RouteGuard>
        } />
        <Route path="academic/tasks" element={
          <RouteGuard requirePermissions="academic:view">
            <AcademicTasksPage />
          </RouteGuard>
        } />
        <Route path="academic/reports" element={
          <RouteGuard requirePermissions="academic:view">
            <AcademicReportsPage />
          </RouteGuard>
        } />
        <Route path="academic/dashboard" element={
          <RouteGuard requirePermissions="academic:view">
            <AcademicDashboardPage />
          </RouteGuard>
        } />

        {/* v1.2-rc2 ④：关于页（所有角色可见，无 guard） */}
        <Route path="about" element={<AboutPage />} />

        {/* v1.2 Batch 4-A：企微会话存档配置（admin） */}
        <Route path="admin/settings/wework-archive" element={
          <RouteGuard requirePermissions="system:wework_archive_manage">
            <WeworkArchivePage />
          </RouteGuard>
        } />

        {/* v1.2 Batch 5：投放 ROI 矩阵 + 花费录入 */}
        <Route path="reports/ad-attribution" element={
          <RouteGuard requirePermissions="marketing:ad_attribution_view">
            <AdAttributionPage />
          </RouteGuard>
        } />
        <Route path="investment/cost-import" element={
          <RouteGuard requirePermissions="marketing:reconcile">
            <CostImportPage />
          </RouteGuard>
        } />
        <Route path="manager/voidage-requests" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <VoidageRequestsPage />
          </RouteGuard>
        } />
        <Route path="manager/broadcast-campaigns" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <BroadcastCampaignsPage />
          </RouteGuard>
        } />
        <Route path="manager/customer-creation-requests" element={
          <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
            <CustomerCreationRequestsPage />
          </RouteGuard>
        } />
        <Route path="im" element={<MessengerPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="ai-reports" element={<AiReportsPage />} />
        <Route path="admin/ai-insights" element={
          <RouteGuard requirePermissions="ai:insights_admin">
            <AiInsightsAdminPage />
          </RouteGuard>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </PermissionProvider>
  );
}
